import React, { useState } from 'react';
import { AlertCircleIcon, BadgeCheckIcon, BarChartIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, CloudIcon, FileTextIcon, GlobeIcon, HeartIcon, LeafIcon, ScaleIcon, ShieldIcon, ThumbsUpIcon, UploadIcon, UsersIcon } from 'lucide-react';
export function ESGProfile({
  vendor
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({
    environmental: true,
    social: false,
    governance: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // Sample ESG data for the vendor
  const [esgData, setEsgData] = useState({
    completionStatus: {
      environmental: 65,
      social: 40,
      governance: 80,
      overall: 62
    },
    certifications: [{
      id: 1,
      name: 'ISO 14001',
      status: 'Verified',
      expirationDate: '2024-06-15',
      description: 'Environmental Management System'
    }, {
      id: 2,
      name: 'B Corp Certification',
      status: 'Pending Verification',
      expirationDate: null,
      description: 'Meets social and environmental performance standards'
    }],
    questionnaire: {
      environmental: [{
        id: 'env-1',
        question: 'Does your company measure its carbon footprint?',
        answer: 'yes',
        details: 'We conduct annual carbon footprint assessments.',
        attachmentName: null
      }, {
        id: 'env-2',
        question: 'Do you have a formal environmental policy?',
        answer: 'yes',
        details: 'Our environmental policy was established in 2018 and is reviewed annually.',
        attachmentName: 'Environmental_Policy_2023.pdf'
      }, {
        id: 'env-3',
        question: 'Has your company set targets for reducing greenhouse gas emissions?',
        answer: 'yes',
        details: 'We aim to reduce emissions by 30% by 2030.',
        attachmentName: null
      }, {
        id: 'env-4',
        question: 'Do you have waste reduction or recycling programs?',
        answer: 'yes',
        details: 'We have implemented comprehensive recycling and waste reduction initiatives.',
        attachmentName: null
      }, {
        id: 'env-5',
        question: 'Does your company use renewable energy?',
        answer: 'partial',
        details: '35% of our energy comes from renewable sources.',
        attachmentName: null
      }],
      social: [{
        id: 'soc-1',
        question: 'Does your company have a diversity and inclusion policy?',
        answer: 'yes',
        details: 'Our D&I policy covers hiring, promotion, and workplace culture.',
        attachmentName: 'Diversity_Policy.pdf'
      }, {
        id: 'soc-2',
        question: 'Do you conduct employee satisfaction surveys?',
        answer: 'yes',
        details: 'We conduct quarterly employee satisfaction surveys.',
        attachmentName: null
      }, {
        id: 'soc-3',
        question: 'Does your company have community engagement programs?',
        answer: 'partial',
        details: "We participate in some community initiatives but don't have a formal program.",
        attachmentName: null
      }, {
        id: 'soc-4',
        question: 'Do you have policies to ensure fair labor practices throughout your supply chain?',
        answer: 'no',
        details: '',
        attachmentName: null
      }, {
        id: 'soc-5',
        question: 'Does your company support employee volunteering?',
        answer: 'yes',
        details: 'Employees receive 2 paid volunteer days per year.',
        attachmentName: null
      }],
      governance: [{
        id: 'gov-1',
        question: 'Does your company have a code of ethics?',
        answer: 'yes',
        details: 'Our code of ethics is reviewed and updated annually.',
        attachmentName: 'Code_of_Ethics.pdf'
      }, {
        id: 'gov-2',
        question: 'Do you have an anti-corruption policy?',
        answer: 'yes',
        details: 'We have a comprehensive anti-corruption policy that applies to all employees and contractors.',
        attachmentName: null
      }, {
        id: 'gov-3',
        question: 'Is your board of directors diverse in terms of gender and ethnicity?',
        answer: 'partial',
        details: '30% of our board members are women, 20% are from underrepresented groups.',
        attachmentName: null
      }, {
        id: 'gov-4',
        question: 'Does your company have a whistleblower policy?',
        answer: 'yes',
        details: 'We have an anonymous whistleblower reporting system.',
        attachmentName: null
      }, {
        id: 'gov-5',
        question: 'Do you conduct regular compliance training for employees?',
        answer: 'yes',
        details: 'All employees complete annual compliance training.',
        attachmentName: null
      }]
    }
  });
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleAnswerChange = (category, questionId, field, value) => {
    setEsgData(prevData => {
      const updatedQuestionnaire = {
        ...prevData.questionnaire
      };
      const categoryQuestions = [...updatedQuestionnaire[category]];
      const questionIndex = categoryQuestions.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        categoryQuestions[questionIndex] = {
          ...categoryQuestions[questionIndex],
          [field]: value
        };
        updatedQuestionnaire[category] = categoryQuestions;
      }
      // Recalculate completion percentage
      const updatedCompletionStatus = calculateCompletionStatus(updatedQuestionnaire);
      return {
        ...prevData,
        questionnaire: updatedQuestionnaire,
        completionStatus: updatedCompletionStatus
      };
    });
  };
  const calculateCompletionStatus = questionnaire => {
    const categories = ['environmental', 'social', 'governance'];
    const results = {};
    categories.forEach(category => {
      const questions = questionnaire[category];
      const answeredQuestions = questions.filter(q => q.answer && q.answer !== '');
      results[category] = Math.round(answeredQuestions.length / questions.length * 100);
    });
    // Calculate overall completion
    const totalQuestions = Object.values(questionnaire).flat().length;
    const totalAnswered = Object.values(questionnaire).flat().filter(q => q.answer && q.answer !== '').length;
    results.overall = Math.round(totalAnswered / totalQuestions * 100);
    return results;
  };
  const handleFileUpload = (category, questionId, file) => {
    if (!file) return;
    handleAnswerChange(category, questionId, 'attachmentName', file.name);
  };
  const handleSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }, 1500);
  };
  const renderQuestionnaireSection = (category, title, icon) => {
    const Icon = icon;
    const questions = esgData.questionnaire[category];
    return <div className="mb-6 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-291">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center cursor-pointer" onClick={() => toggleSection(category)} data-id="element-292">
          <div className="flex items-center" data-id="element-293">
            <Icon className="h-6 w-6 text-indigo-500 mr-3" data-id="element-294" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-295">
              {title}
            </h3>
            <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-296">
              {esgData.completionStatus[category]}% Complete
            </span>
          </div>
          {expandedSections[category] ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-297" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-298" />}
        </div>
        {expandedSections[category] && <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-299">
            <div className="space-y-6" data-id="element-300">
              {questions.map(question => <div key={question.id} className="bg-gray-50 p-4 rounded-lg" data-id="element-301">
                  <div className="mb-2 font-medium text-gray-900" data-id="element-302">
                    {question.question}
                  </div>
                  <div className="mt-3 space-y-4" data-id="element-303">
                    <div className="flex items-center space-x-4" data-id="element-304">
                      <label className="inline-flex items-center" data-id="element-305">
                        <input type="radio" name={question.id} value="yes" checked={question.answer === 'yes'} onChange={() => handleAnswerChange(category, question.id, 'answer', 'yes')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-306" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-307">Yes</span>
                      </label>
                      <label className="inline-flex items-center" data-id="element-308">
                        <input type="radio" name={question.id} value="no" checked={question.answer === 'no'} onChange={() => handleAnswerChange(category, question.id, 'answer', 'no')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-309" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-310">No</span>
                      </label>
                      <label className="inline-flex items-center" data-id="element-311">
                        <input type="radio" name={question.id} value="partial" checked={question.answer === 'partial'} onChange={() => handleAnswerChange(category, question.id, 'answer', 'partial')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-312" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-313">
                          Partial
                        </span>
                      </label>
                    </div>
                    {question.answer && question.answer !== 'no' && <div data-id="element-314">
                        <label htmlFor={`${question.id}-details`} className="block text-sm font-medium text-gray-700" data-id="element-315">
                          Additional Details
                        </label>
                        <textarea id={`${question.id}-details`} rows={2} value={question.details} onChange={e => handleAnswerChange(category, question.id, 'details', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Provide additional details about your answer..." data-id="element-316" />
                      </div>}
                    {question.answer && question.answer !== 'no' && <div data-id="element-317">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-318">
                          Supporting Documentation
                        </label>
                        <div className="mt-1 flex items-center" data-id="element-319">
                          {question.attachmentName ? <div className="flex items-center bg-gray-100 p-2 rounded" data-id="element-320">
                              <FileTextIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-321" />
                              <span className="text-sm text-gray-600" data-id="element-322">
                                {question.attachmentName}
                              </span>
                              <button type="button" onClick={() => handleAnswerChange(category, question.id, 'attachmentName', null)} className="ml-2 text-sm text-red-600 hover:text-red-900" data-id="element-323">
                                Remove
                              </button>
                            </div> : <div className="flex items-center" data-id="element-324">
                              <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none" data-id="element-325">
                                <span data-id="element-326">Upload a file</span>
                                <input type="file" className="sr-only" onChange={e => handleFileUpload(category, question.id, e.target.files[0])} data-id="element-327" />
                              </label>
                              <p className="pl-1 text-xs text-gray-500" data-id="element-328">
                                PDF, DOCX, XLSX up to 10MB
                              </p>
                            </div>}
                        </div>
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>}
      </div>;
  };
  const renderOverview = () => {
    return <div data-id="element-329">
        <div className="mb-6 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-330">
          <div className="px-4 py-5 sm:px-6" data-id="element-331">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-332">
              ESG Completion Status
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-333">
              Your current ESG profile completion status
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-334">
            <div className="mb-4" data-id="element-335">
              <div className="flex justify-between items-center mb-1" data-id="element-336">
                <div className="text-sm font-medium text-gray-700" data-id="element-337">
                  Overall Completion
                </div>
                <div className="text-sm font-medium text-gray-700" data-id="element-338">
                  {esgData.completionStatus.overall}%
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-339">
                <div className="bg-indigo-600 h-2.5 rounded-full" style={{
                width: `${esgData.completionStatus.overall}%`
              }} data-id="element-340"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-341">
              <div className="bg-green-50 p-4 rounded-lg" data-id="element-342">
                <div className="flex items-center mb-2" data-id="element-343">
                  <LeafIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-344" />
                  <span className="font-medium text-green-700" data-id="element-345">
                    Environmental
                  </span>
                </div>
                <div className="flex justify-between items-center mb-1" data-id="element-346">
                  <div className="text-xs text-green-600" data-id="element-347">Completion</div>
                  <div className="text-xs font-medium text-green-600" data-id="element-348">
                    {esgData.completionStatus.environmental}%
                  </div>
                </div>
                <div className="w-full bg-green-200 rounded-full h-1.5" data-id="element-349">
                  <div className="bg-green-600 h-1.5 rounded-full" style={{
                  width: `${esgData.completionStatus.environmental}%`
                }} data-id="element-350"></div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg" data-id="element-351">
                <div className="flex items-center mb-2" data-id="element-352">
                  <UsersIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-353" />
                  <span className="font-medium text-blue-700" data-id="element-354">Social</span>
                </div>
                <div className="flex justify-between items-center mb-1" data-id="element-355">
                  <div className="text-xs text-blue-600" data-id="element-356">Completion</div>
                  <div className="text-xs font-medium text-blue-600" data-id="element-357">
                    {esgData.completionStatus.social}%
                  </div>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5" data-id="element-358">
                  <div className="bg-blue-600 h-1.5 rounded-full" style={{
                  width: `${esgData.completionStatus.social}%`
                }} data-id="element-359"></div>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg" data-id="element-360">
                <div className="flex items-center mb-2" data-id="element-361">
                  <ScaleIcon className="h-5 w-5 text-purple-500 mr-2" data-id="element-362" />
                  <span className="font-medium text-purple-700" data-id="element-363">
                    Governance
                  </span>
                </div>
                <div className="flex justify-between items-center mb-1" data-id="element-364">
                  <div className="text-xs text-purple-600" data-id="element-365">Completion</div>
                  <div className="text-xs font-medium text-purple-600" data-id="element-366">
                    {esgData.completionStatus.governance}%
                  </div>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-1.5" data-id="element-367">
                  <div className="bg-purple-600 h-1.5 rounded-full" style={{
                  width: `${esgData.completionStatus.governance}%`
                }} data-id="element-368"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-369">
          <div className="px-4 py-5 sm:px-6" data-id="element-370">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-371">
              ESG Certifications
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-372">
              Your sustainability and compliance certifications
            </p>
          </div>
          <div className="border-t border-gray-200" data-id="element-373">
            <ul className="divide-y divide-gray-200" data-id="element-374">
              {esgData.certifications.map(cert => <li key={cert.id} className="px-4 py-4 sm:px-6" data-id="element-375">
                  <div className="flex items-center justify-between" data-id="element-376">
                    <div className="flex items-center" data-id="element-377">
                      <BadgeCheckIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-378" />
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-379">
                        {cert.name}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-380">
                      <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${cert.status === 'Verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-381">
                        {cert.status}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-382">
                    <div className="sm:flex" data-id="element-383">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-384">
                        {cert.description}
                      </p>
                    </div>
                    {cert.expirationDate && <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-385">
                        <p data-id="element-386">
                          Expires:{' '}
                          {new Date(cert.expirationDate).toLocaleDateString()}
                        </p>
                      </div>}
                  </div>
                </li>)}
              <li className="px-4 py-4 sm:px-6" data-id="element-387">
                <button type="button" className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-388">
                  <UploadIcon className="h-4 w-4 mr-2" data-id="element-389" />
                  Add New Certification
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>;
  };
  return <div data-id="element-390">
      <div className="mb-8" data-id="element-391">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-392">ESG Profile</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-393">
          Manage your Environmental, Social, and Governance information
        </p>
      </div>
      {showSuccessMessage && <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4" data-id="element-394">
          <div className="flex" data-id="element-395">
            <div className="flex-shrink-0" data-id="element-396">
              <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-397" />
            </div>
            <div className="ml-3" data-id="element-398">
              <p className="text-sm text-green-700" data-id="element-399">
                Your ESG profile has been successfully updated.
              </p>
            </div>
          </div>
        </div>}
      <div className="mb-6" data-id="element-400">
        <nav className="flex space-x-4" aria-label="Tabs" data-id="element-401">
          <button onClick={() => setActiveTab('overview')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'overview' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-402">
            Overview
          </button>
          <button onClick={() => setActiveTab('questionnaire')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'questionnaire' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-403">
            ESG Questionnaire
          </button>
        </nav>
      </div>
      {activeTab === 'overview' ? renderOverview() : <form onSubmit={handleSubmit} data-id="element-404">
          {renderQuestionnaireSection('environmental', 'Environmental', LeafIcon)}
          {renderQuestionnaireSection('social', 'Social', UsersIcon)}
          {renderQuestionnaireSection('governance', 'Governance', ScaleIcon)}
          <div className="flex justify-end" data-id="element-405">
            <button type="submit" disabled={isSubmitting} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" data-id="element-406">
              {isSubmitting ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-407">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-408"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-409"></path>
                  </svg>
                  Saving...
                </> : 'Save ESG Profile'}
            </button>
          </div>
        </form>}
    </div>;
}